/**
@format
@flow local-strict
*/
import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import BannerNotification from '@ziynx/components/notifications';
import ActionTypes from '@ziynx/action-types';
import { useParams } from 'react-router-dom';
import { eventNames } from '@ziynx/analytics';
import { Button } from '@ziynx/components/buttons';
import Colors from '@ziynx/theme/colors';
import Input from '@ziynx/components/inputs';
import Link from '@ziynx/components/links';
import type { ManagedProps } from '@ziynx/flow-types/appManager';
import { withAppManager, nameInputsRegex, emailRegex } from '@ziynx/components/utilities';
import { Body } from '@ziynx/components/typography';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { ContentContainer } from '@ziynx/components/layout';
import { Form, FormRow } from '@ziynx/components/forms';
import Image from '@ziynx/components/images';
import logo from '../../assets/ZIYNX_LOGO_2.png';
import logoSmall from '../../assets/ZIYNX_SYMBOL_2 2x.png';

type Props = $ReadOnly<{|

  /**
   * Navigation stack
   */
  history: {
    push: (pathName: string, {email: string, password: string }) => void,
    replace: (pathName: string, {from: string}) => void,
  },
|}>;

// display table so IE is not left out
const FormSectionWrapper = styled.div`
  display: table;
  padding: ${({ isMobileSmall }) => (isMobileSmall ? '0px 16px 40px' : '0px 40px 40px')};
  ${({ isMobile }) => !isMobile && 'margin: 0 auto;'}
`;

const StyledBannerWrapper = styled.div`
  z-index: 996;
  width: 100%;
  height: 0;
`;

const StyledBodyWrapper = styled.div`
  ${({ lastChild }) => !lastChild && 'padding-bottom: 40px;'}
  ${({ center }) => center && 'text-align: center;'}
`;

const StyledButton = styled(Button)`
  margin-bottom: 24px;
`;

const ImageWrapper = styled.div`
  padding-bottom: 64px;
  padding-top: ${({ isMobile }) => (isMobile ? 32 : 48)}px;
`;

/**
 * Time for banner to setTimeout
 */
const bannerTimeOut = 5000;

/**
 * Sign up screen for user to create new account
 * Each input will be validated on first blur event,
 * then will be re-validated on change event after if needed (onTouched mode)
 * @see https://react-hook-form.com/api
 */
const InvitedSignupScreen = (props: ManagedProps<Props>): React$Node => {
  const { history, viewport } = props;
  const dispatch = useDispatch();
  const { memberId } = useParams();
  const isMobile = viewport === 'mobileSmall' || viewport === 'mobile';
  const isMobileSmall = viewport === 'mobileSmall';

  useEffect(() => {
    dispatch({
      type: ActionTypes.teamMember.getInvite.START,
      token: memberId,
      authorization: 'none',
    });
  }, []);

  const teamMemberService = useSelector(({ teamMember }) => teamMember[memberId] || {},
    shallowEqual);

  const registrationService = useSelector(({ auth }) => auth[ActionTypes.auth.signUp.GROUP]
  || {}, shallowEqual);

  useEffect(() => {
    if (teamMemberService?.userStatus === 'EXPIRED' || teamMemberService?.error?.status === 404) {
      history.replace('/', { from: '/invite' });
    }
  }, [teamMemberService?.userStatus, teamMemberService?.error?.status]);

  const {
    handleSubmit, control, errors, setError, clearErrors, formState, register, watch,
  } = useForm({ mode: 'onTouched' });

  // state for banner error message based on hasError
  const [bannerErrorMessage, setBannerErrorMessage] = useState('');
  // State to keep track if banner should be rendered
  const [shouldRenderBanner, setRenderBanner] = useState(false);
  // Shows if waiting for response from AWS
  const [isLoading, setIsLoading] = useState(false);

  const [submittedData, setSubmittedData] = useState({ email: '', password: '', invited: true });

  // Keeps track of all the input user modified
  const { dirtyFields, isDirty } = formState;

  const watchPassword = watch('password');
  /**
   * Function fired when user hits create account button.
   */
  const onSubmit = async (data: {
    firstName: string,
    lastName: string,
    email: string,
    password: string
  }): Promise<any> => {
    setIsLoading(true);
    const {
      password,
      email,
      firstName,
      lastName,
    } = data || {};
    const trimmedEmail = email?.trim();
    setSubmittedData({ email: trimmedEmail, password, invited: true });

    dispatch({
      type: ActionTypes.auth.signUp.START,
      username: trimmedEmail,
      password,
      attributes: {
        email: trimmedEmail,
        given_name: firstName?.trim(),
        family_name: lastName?.trim(),
      },
    });
  };

  useEffect(() => {
    if (!registrationService.error) {
      return;
    }
    setRenderBanner(false);
    setIsLoading(false);
    switch (registrationService?.error.code) {
    case 'InvalidPasswordException':
    case 'InvalidParameterException':
      setError('password', {
        type: 'manual',
        message: registrationService?.error?.message,
      });
      break;

    case 'UsernameExistsException':
      setError('email', {
        type: 'manual',
        message: registrationService?.error?.message,
      });
      setBannerErrorMessage(registrationService?.error?.message);
      setRenderBanner(true);
      break;

    case 'InvalidEmailRoleAccessPolicyException':
      setError('email', {
        type: 'manual',
        message: registrationService?.error?.message,
      });
      break;

    case 'CodeDeliveryFailureException':
    default:
      setBannerErrorMessage(registrationService?.error?.message);
      setRenderBanner(true);
      break;
    }

    return () => dispatch({ type: ActionTypes.auth.clearErrors });
  }, [registrationService?.error]);

  useEffect(() => {
    if (!registrationService?.success) return;
    dispatch({
      type: ActionTypes.auth.clear,
    });
    dispatch({
      type: ActionTypes.analytics.logEvent.START,
      name: eventNames?.onboarding_create_account_invited,
    });
    history.push('/confirm-code', submittedData);
  }, [registrationService?.success]);

  /**
   * Function to map out FormRows
   * that holds the Controller of each types of inputs
   * based on inputsData
   */

  const renderInputsControllers = () => (
    <>
      <FormRow paddingBottom="40px">
        <Controller
          control={control}
          render={({
            onChange, value, onFocus,
          }) => {
            const onInputChange = (event: SyntheticInputEvent<HTMLInputElement>): void => {
              (event?.target: HTMLInputElement);
              onChange(event);
            };

            const onInputFocus = (): any => {
              if (errors?.firstName) clearErrors('firstName');
              return onFocus;
            };
            return (
              <Input
                variant="text"
                error={!!errors?.firstName}
                errorLabel={errors?.firstName?.message}
                inputName="firstName"
                label="First name"
                onChange={onInputChange}
                onFocus={onInputFocus}
                value={value}
                required
              />
            );
          }}
          name="firstName"
          defaultValue={teamMemberService?.firstName || ' '}
          rules={{
            required: 'Please enter a first name',
            pattern: {
              value: nameInputsRegex,
              message: 'Please enter a valid first name',
            },
          }}
        />
      </FormRow>
      <FormRow paddingBottom="40px">
        <Controller
          control={control}
          render={({
            onChange, value, onFocus,
          }) => {
            const onInputChange = (event: SyntheticInputEvent<HTMLInputElement>): void => {
              (event?.target: HTMLInputElement);
              onChange(event);
            };

            const onInputFocus = (): any => {
              if (errors.lastName) clearErrors('lastName');
              return onFocus;
            };
            return (
              <Input
                variant="text"
                error={!!errors?.lastName}
                errorLabel={errors?.lastName?.message}
                inputName="lastName"
                onChange={onInputChange}
                onFocus={onInputFocus}
                label="Last name"
                value={value}
                required
              />
            );
          }}
          name="lastName"
          defaultValue={teamMemberService?.lastName || ' '}
          rules={{
            required: 'Please enter a last name',
            pattern: {
              value: nameInputsRegex,
              message: 'Please enter a valid last name',
            },
          }}
        />
      </FormRow>
      <FormRow paddingBottom="40px">
        <Controller
          control={control}
          render={({
            onChange, value, onFocus,
          }) => {
            /**
             * Function to clear any errors of inputs
             * when on focus event, if any
             */
            const onInputChange = (event: SyntheticInputEvent<HTMLInputElement>): void => {
              (event?.target: HTMLInputElement);
              onChange(event);
            };

            const onInputFocus = (): any => {
              if (errors?.email) clearErrors('email');
              return onFocus;
            };
            return (
              <Input
                variant="email"
                disabled
                error={!!errors?.email}
                errorLabel={errors?.email?.message}
                inputName="email"
                label="Email"
                onChange={onInputChange}
                onFocus={onInputFocus}
                value={value}
                required
              />
            );
          }}
          name="email"
          defaultValue={teamMemberService?.email || ' '}
          rules={{
            required: 'Please enter an email',
            pattern: {
              value: emailRegex,
              message: 'Please enter a valid email',
            },
          }}
        />
      </FormRow>
      <FormRow paddingBottom="32px">
        <Controller
          control={control}
          render={({
            onChange, value, onFocus,
          }) => {
            const onInputChange = (event: SyntheticInputEvent<HTMLInputElement>): void => {
              (event?.target: HTMLInputElement);
              onChange(event?.target?.value);
            };
            /**
             * Function to clear any errors of inputs
             * when on focus event, if any
             */
            const onInputFocus = (): any => {
              if (errors?.password) clearErrors('password');
              return onFocus;
            };
            return (
              <Input
                variant="password"
                error={!!errors?.password}
                errorLabel={errors?.password?.message}
                inputName="password"
                label="Password"
                onChange={onInputChange}
                onFocus={onInputFocus}
                value={value}
                required
                {...register('password')}
              />
            );
          }}
          name="password"
          defaultValue=""
          rules={{
            required: 'Please enter a password',
            pattern: {
              value: /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])[0-9a-zA-Z!@#$%^ˆ&*?_\-+=()\\|<>/{}[\]:;.,~˜`'"]{8,}$/im,
              message: 'Password is too weak. Use at least 8 characters and 1 number',
            },
          }}
        />
      </FormRow>
    </>
  );
  /**
   * Function to check if dirtyFields length equals to inputsData length.
   * Returns true if not -> disable button
   */
  const isDisabled = (): boolean => {
    if (!dirtyFields || isLoading || !watchPassword) return true;

    return Object.keys(dirtyFields).length < 1 || !isDirty;
  };

  /**
   * Function to watch if an input has error
   * after user submit upon validation.
   * Errors object is {} by default - which means no error
   * Returns true if length of errors object != 0
   * and false if empty
   */
  const isError = (): boolean => !!Object.keys(errors).length;

  /**
   * To render ziynx logo as image
   */
  const renderLogo = (): React$Node => {
    if (isMobile) {
      return (
        <Image src={logoSmall} width="24px" height="24px" alt="company-logo" />
      );
    }
    return (
      <Image src={logo} width="114px" height="24px" alt="company-logo" />
    );
  };

  if (teamMemberService?.fetching) return null;

  return (
    <>
      <Helmet>
        <title>Sign Up | ZIYNX</title>
      </Helmet>
      <ContentContainer isVertical offsetTop={0}>
        {shouldRenderBanner && (
          <StyledBannerWrapper>
            <BannerNotification animated timeout={bannerTimeOut} variant="error">
              {bannerErrorMessage}
            </BannerNotification>
          </StyledBannerWrapper>
        )}
        <FormSectionWrapper
          isMobile={isMobile}
          isMobileSmall={isMobileSmall}
          hasBanner={shouldRenderBanner}
        >
          <ImageWrapper isMobile={isMobile}>
            {renderLogo()}
          </ImageWrapper>
          <Form
            formID="invited-sign-up"
            title="Create your account"
          >
            {/* <FormSubTitle>
                <Body medium color={Colors.GrayDark}>
                  Lorem ipsum dolor sit amet, consectetur adipisicing elit, sed
                  do eiusmod tempor
                </Body>
              </FormSubTitle> */}

            {renderInputsControllers()}

            <StyledBodyWrapper>
              <Body medium color={Colors.GrayDark}>
                By joining, you agree to our ZIYNX
                <Link href="https://resources.teamziynx.com/legal/TermsOfService" target="_blank"> User Agreement, </Link>
                <Link href="https://resources.teamziynx.com/legal/EndUserLicenseAgreement" target="_blank"> End User License, </Link>
                and
                <Link href="https://resources.teamziynx.com/legal/PrivacyPolicy" target="_blank"> Privacy Policy.</Link>
              </Body>
            </StyledBodyWrapper>

            <StyledButton
              variant="primary"
              disabled={isDisabled() || isError()}
              isLoading={isLoading}
              width="100%"
              form="invited-sign-up"
              type="submit"
              onClick={handleSubmit(onSubmit)}
            >
              Create account
            </StyledButton>

            <StyledBodyWrapper center lastChild>
              <Body medium color={Colors.GrayVeryDark}>
                Already have an account?&nbsp;
                <Link variant="navigation" activeOnlyWhenExact label="Log in" to="/" />
              </Body>
            </StyledBodyWrapper>
          </Form>
        </FormSectionWrapper>
      </ContentContainer>
    </>
  );
};

InvitedSignupScreen.defaultProps = {
  viewport: 'desktop',
};

export default
(withAppManager(InvitedSignupScreen): React$ComponentType<React$Config<ManagedProps<Props>,
typeof InvitedSignupScreen.defaultProps>
>);
